import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  buildWipLowCareDocumentId, subscribeWipTrackingStock, WIP_TRACKING_COLLECTION,
} from '../services/wipTrackingFirestore';
import { normalizeWipStock } from '../utils/wipMonitor';

export const WipStockContext = createContext(null);
export default function useWipFirestoreStock() {
  const provided = useContext(WipStockContext);
  const { plant: routePlant = '', factory: routeFactory = '' } = useParams();
  const authPlant = useSelector((state) => state.auth?.plant_id || '');
  const authFactory = useSelector((state) => state.auth?.factory || '');
  const plantId = routePlant || authPlant;
  const factory = routeFactory || authFactory;
  const [documents, setDocuments] = useState([]);
  const [clock, setClock] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const scope = useMemo(() => ({ plantId, factory }), [factory, plantId]);
  const scopeReady = Boolean(plantId && factory);
  const documentId = scopeReady ? buildWipLowCareDocumentId(scope) : '';

  useEffect(() => {
    if (provided) return undefined;
    if (!scopeReady) {
      setDocuments([]);
      setLoading(false);
      return undefined;
    }
    setDocuments([]);
    setLoading(true);
    setError('');
    return subscribeWipTrackingStock({
      scope,
      onData: (nextDocuments) => {
        setDocuments(nextDocuments);
        setLoading(false);
      },
      onError: (nextError) => {
        setError(nextError?.message || 'Unable to load WIP Tracking data from Firestore.');
        setLoading(false);
      },
    });
  }, [scope, scopeReady, provided]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const items = useMemo(() => normalizeWipStock(
    documents,
    scope,
    clock,
  ), [clock, documents, scope]);

  return provided || {
    items,
    loading,
    error,
    clock,
    collectionName: WIP_TRACKING_COLLECTION,
    documentId,
    scope,
    scopeReady,
  };
}

